import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicShell from '../components/PublicShell';
import { Button } from '../components/ui/button';
import { fbqTrack } from '../lib/fbpixel';
import { getWhatsappGroupUrl, getFreeTelegramUrl } from '../config/brand';
import { BookOpen, Users, Send, ChevronRight, CheckCircle2 } from 'lucide-react';

const LESSON_IMAGES = ['structure', 'keylevels', 'liquidity', 'rangecandle', 'time', 'risk'];

export default function LearnPage() {
    const { t, i18n } = useTranslation();
    const LESSON = t('learn.sections', { returnObjects: true }) || [];
    const TASKS = t('learn.tasks', { returnObjects: true }) || [];
    useEffect(() => { fbqTrack('ViewContent', { content_name: 'Free Lesson: Market Structure Basics', content_category: 'education' }); }, []);
    return (
        <PublicShell eyebrow={t('learn.eyebrow')} title={t('learn.title')} testId="learn-page">
            <div className="rounded-2xl border border-[#FFB800]/30 bg-[#FFB800]/5 p-5 flex items-start gap-3" data-testid="learn-intro">
                <BookOpen className="w-5 h-5 text-[#FFB800] shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-300">{t('learn.intro')}</p>
            </div>
            {LESSON.map(({ h, body }, i) => (
                <section key={h} className="pt-6" data-testid="learn-section">
                    <h2 className="text-lg md:text-xl font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{h}</h2>
                    {LESSON_IMAGES[i] && (
                        <img src={`/learn/${LESSON_IMAGES[i]}.jpg`} alt={h} loading="lazy" className="w-full rounded-2xl border border-[#FFB800]/20 shadow-[0_0_40px_rgba(255,184,0,0.08)] mb-4" data-testid={`learn-image-${LESSON_IMAGES[i]}`} />
                    )}
                    <p>{body}</p>
                </section>
            ))}
            <section className="pt-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('learn.practiceTitle')}</h2>
                <ul className="space-y-2">
                    {TASKS.map((x) => (
                        <li key={x} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-[#FFB800] shrink-0 mt-0.5" />{x}</li>
                    ))}
                </ul>
            </section>
            <section className="pt-10 rounded-3xl border border-zinc-800 bg-zinc-950 p-7 text-center" data-testid="learn-cta">
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#FFB800] mb-3">{t('learn.ctaOverline')}</p>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('learn.ctaTitle')}</h2>
                <p className="text-sm text-zinc-400 mb-6 max-w-xl mx-auto">{t('learn.ctaText')}</p>
                <div className="flex flex-wrap justify-center gap-3">
                    <Link to="/register"><Button className="rounded-full px-8 py-6 h-auto bg-[#FFB800] text-black font-black hover:bg-[#FFC933]" data-testid="learn-register-btn">{t('learn.ctaRegister')} <ChevronRight className="w-4 h-4 ms-1" /></Button></Link>
                    <a href={getWhatsappGroupUrl(i18n.language)} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-full px-6 py-6 h-auto border-zinc-700 text-white bg-transparent hover:bg-zinc-900" data-testid="learn-whatsapp-btn"><Users className="w-4 h-4 me-2" /> {t('learn.ctaWhatsapp')}</Button></a>
                    <a href={getFreeTelegramUrl(i18n.language)} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-full px-6 py-6 h-auto border-zinc-700 text-white bg-transparent hover:bg-zinc-900" data-testid="learn-telegram-btn"><Send className="w-4 h-4 me-2" /> {t('learn.ctaTelegram')}</Button></a>
                </div>
            </section>
        </PublicShell>
    );
}
